import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import PageHero from "../components/PageHero";
import ContactCTA from "../components/ContactCTA";
import { productCategories } from "../data/mock";
import { Check, ArrowRight, ArrowUpRight } from "lucide-react";

const ServiceDetail = () => {
  const { slug } = useParams();
  const product = productCategories.find((p) => p.slug === slug);

  if (!product) return <Navigate to="/services" replace />;

  const others = productCategories.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <PageLayout>
      <PageHero
        badge="Service Detail"
        title={product.title}
        subtitle={product.description}
        breadcrumbs={[
          { label: "Services", to: "/services" },
          { label: product.title },
        ]}
      />

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
          <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-2xl shadow-emerald-500/10">
            <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0a2e1f]/40 via-transparent to-transparent" />
          </div>
          <div>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] mb-4 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
              Overview
            </span>
            <h2 className="text-2xl md:text-3xl font-bold mb-4 tracking-tight" style={{ color: "#0a2e1f" }}>
              Built for the toughest jobs
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">{product.longDescription}</p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white text-sm font-semibold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-[1.03] transition-all"
              style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
            >
              Request a Quote <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Features & Applications */}
      <section className="py-20" style={{ backgroundColor: "#f7faf8" }}>
        <div className="max-w-[1200px] mx-auto px-6 grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl p-8 border border-slate-100">
            <h3 className="text-xl font-bold mb-5" style={{ color: "#0a2e1f" }}>
              Key Features
            </h3>
            <ul className="space-y-3">
              {product.features.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                    style={{ background: "linear-gradient(135deg, #10b981, #047857)" }}
                  >
                    <Check size={12} className="text-white" />
                  </div>
                  <span className="leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-3xl p-8 border border-slate-100">
            <h3 className="text-xl font-bold mb-5" style={{ color: "#0a2e1f" }}>
              Typical Applications
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {product.applications.map((a, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 text-sm text-slate-700 px-4 py-3 rounded-xl bg-emerald-50/50 border border-emerald-100"
                >
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] mb-3 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
                More Services
              </span>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "#0a2e1f" }}>
                Explore other capabilities
              </h2>
            </div>
            <Link to="/services" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:gap-2 transition-all">
              View all <ArrowRight size={15} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {others.map((p) => (
              <Link
                key={p.id}
                to={`/services/${p.slug}`}
                className="group block bg-white rounded-3xl overflow-hidden border border-slate-100 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/10 hover:-translate-y-1 transition-all duration-500"
              >
                <div className="relative aspect-[5/3] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowUpRight size={18} className="text-emerald-700" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-base font-bold mb-1 group-hover:text-emerald-700 transition-colors" style={{ color: "#0a2e1f" }}>
                    {p.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed line-clamp-2">{p.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ContactCTA />
    </PageLayout>
  );
};

export default ServiceDetail;
